import { createRouter, createWebHistory } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { supabase } from '../supabaseClient'
import { useAuthProfile } from '../composables/useAuthProfile'
import { getAuthUserShared } from '../composables/useAuthUser.js'
import { getFamilyMembership, abandonFamilyMembershipInFlight } from '../composables/useFamilyMembership.js'
import { getApiBase } from '../utils/apiBase.js'
import { attachLoginTelemetryToRouter, markLoginStage, reportGuardDeadline } from '../utils/loginTelemetry.js'
import { createGuardSettler, safeInternalReturnPath } from '../utils/guardDeadline.js'
import { retryWhileMissing } from '../utils/retryWhileMissing.js'
import { GuardAccessError, classifyGetUserResult, assertQueryOk, guardErrorExit } from '../utils/guardAccess.js'
import { createProfileEmailSync } from '../utils/profileEmailSync.js'
import Phase2Login from '../components/auth/Phase2Login.vue'
import ResetPassword from '../components/auth/ResetPassword.vue'
import ConnectionError from '../views/ConnectionError.vue'
import { isStaleChunkErrorMessage } from '../utils/staleChunkError.js'

// メインアプリケーション画面
import Top from '../views/Top.vue'
import Practice from '../views/Practice.vue'
import PastVideos from '../views/PastVideos.vue'
import Recording from '../views/Recording.vue'
import MyPage from '../views/MyPage.vue'
import Calendar from '../views/Calendar.vue'
import CancelSubscription from '../views/CancelSubscription.vue'
// テストページのインポートをコメントアウト
// import TestRecording from '../views/TestRecording.vue'
// import SimpleTestRecording from '../views/SimpleTestRecording.vue'
// import TestRecordingSimple from '../views/TestRecordingSimple.vue'

// 講師用画面
import InstructorLogin from '../components/auth/InstructorLogin.vue'
import InstructorSignup from '../views/instructor/InstructorSignup.vue'
import InstructorDashboard from '../views/instructor/InstructorDashboard.vue'
import InstructorHome from '../views/instructor/InstructorHome.vue'
import NotificationSettings from '../views/instructor/NotificationSettings.vue'
import VacationSettings from '../views/instructor/VacationSettings.vue'
import InstructorHistory from '../views/instructor/InstructorHistory.vue'
import InstructorMypage from '../views/instructor/InstructorMypage.vue'
import InstructorDay0Submissions from '../views/instructor/InstructorDay0Submissions.vue'
import Billing from '../views/Billing.vue'
import SignupSuccess from '../views/SignupSuccess.vue'
import SignupComplete from '../views/SignupComplete.vue'

// 管理画面
import InstructorAssignments from '../views/admin/InstructorAssignments.vue'
import AdminHolidays from '../views/AdminHolidays.vue'

// 管理者（manager）画面
import ManagerDashboard from '../views/manager/ManagerDashboard.vue'
import ManagerInstructors from '../views/manager/ManagerInstructors.vue'
import ManagerSubmissions from '../views/manager/ManagerSubmissions.vue'
import ManagerFeedbackPlaybacks from '../views/manager/ManagerFeedbackPlaybacks.vue'
import ManagerTrialProgress from '../views/manager/ManagerTrialProgress.vue'
import ManagerUsers from '../views/manager/ManagerUsers.vue'
import ManagerOnboardingProgress from '../views/manager/ManagerOnboardingProgress.vue'
import ManagerLineFunnel from '../views/manager/ManagerLineFunnel.vue'
import ManagerShikokuFunnel from '../views/manager/ManagerShikokuFunnel.vue'
import ManagerProcessingStatus from '../views/manager/ManagerProcessingStatus.vue'
import ManagerBillingOverview from '../views/manager/ManagerBillingOverview.vue'
import ManagerLearners from '../views/manager/ManagerLearners.vue'
import ManagerCanceledLearners from '../views/manager/ManagerCanceledLearners.vue'
import ManagerIndividualAnalysis from '../views/manager/ManagerIndividualAnalysis.vue'

// /training と /gallery 機能
import Training from '../views/Training.vue'
import TrainingDay from '../views/TrainingDay.vue'
import TrainingTask from '../views/TrainingTask.vue'
import Gallery from '../views/Gallery.vue'
import ParentChecklist from '../views/ParentChecklist.vue'

/** 指定 ms で必ず reject する。ネットワーク待ちが永久にハングして beforeEach が完了しなくなるのを防ぐ */
function withTimeout(promise, ms, message) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message || 'timeout')), ms)
    promise.then(
      (v) => { clearTimeout(timer); resolve(v) },
      (e) => { clearTimeout(timer); reject(e) }
    )
  })
}

// メールアドレスの変更の反映（ログインのたびのプロフィール保存をやめたため。食い違うときだけ更新。指示書 A6）
const syncProfileEmailIfChanged = createProfileEmailSync({
  update: (userId, email) => supabase
    .from('users_phase2')
    .update({ email, updated_at: new Date().toISOString() })
    .eq('id', userId)
})

/**
 * 初期登録（Onboarding）の入力がそろっているか: 保護者名と、最初に登録した子どもの名前・ふりがな・生年月日。
 * LINE連携は、ログイン・画面移動の条件にしない（連携していなくても、解除していても、ログインできる）。
 * 初回OnboardingはLINE確認後に必須情報を保存する。保存前の直リンク・再ログインではこのゲートを通さない。
 * 取得に失敗したときは、未入力と断定しない（GuardAccessError → 回復画面）。
 */
async function hasRequiredOnboardingInfo(userId, profile) {
  const hasParentName = !!(profile?.full_name && String(profile.full_name).trim())
  if (!hasParentName) return false
  const { data: learner } = assertQueryOk(await supabase
    .from('learners')
    .select('id, name, name_kana, birth_date')
    .eq('parent_id', userId)
    .eq('type', 'child')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle(), 'learner_query_failed')
  return (
    !!learner &&
    !!(learner.name && String(learner.name).trim()) &&
    !!(learner.name_kana && String(learner.name_kana).trim()) &&
    !!(learner.birth_date && String(learner.birth_date).trim())
  )
}

const routes = [
  {
    path: '/eiken',
    name: 'Eiken',
    component: () => import('../views/Eiken.vue'),
    meta: { requiresAuth: true, hidden: true }
  },
  {
    path: '/billing',
    name: 'Billing',
    component: Billing,
    meta: { requiresAuth: true }
  },
  {
    path: '/signup/success',
    name: 'SignupSuccess',
    component: SignupSuccess
  },
  {
    path: '/signup/complete',
    name: 'SignupComplete',
    component: SignupComplete
  },
  {
    path: '/signup/verify',
    name: 'SignupVerify',
    component: () => import('../views/SignupVerify.vue')
  },
  {
    path: '/day0',
    name: 'Day0',
    component: () => import('../views/Day0.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('../views/Onboarding.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/practice-setup',
    name: 'PracticeSetup',
    component: () => import('../views/PracticeSetup.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/parent-checklist',
    name: 'ParentChecklist',
    component: ParentChecklist
  },
  {
    path: '/',
    name: 'Login',
    component: Phase2Login
  },
  // 管理者（manager）専用ログイン入口
  // ログイン後の遷移先は Phase2Login 側で /manager に固定（一般ユーザーは権限チェックで弾かれる）
  {
    path: '/manager/login',
    name: 'ManagerLogin',
    component: Phase2Login
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword
  },
  // 回復画面（認証ガードが期限内に終わらなかったときの出口）。ガードは、この画面を確認なしで通す
  {
    path: '/connection-error',
    name: 'ConnectionError',
    // 遅延読み込みにしない。通信が悪くてガードの確認に失敗したときの行き先なので、画面のファイルまで
    // 通信で取りに行くと一緒に失敗して白い画面になる（Sentry APP-JAVASCRIPT-VUE-15 / -M）
    component: ConnectionError
  },
  {
    path: '/top',
    name: 'Top',
    component: Top,
    meta: { requiresAuth: true }
  },
  {
    path: '/practice',
    name: 'Practice',
    component: Practice,
    meta: { requiresAuth: true }
  },
  // これまでのレッスン動画（終えたDayを見るだけ。会員の確認は練習画面と同じガード）
  {
    path: '/past-videos',
    name: 'PastVideos',
    component: PastVideos,
    meta: { requiresAuth: true }
  },
  {
    path: '/recording',
    name: 'Recording',
    component: Recording,
    meta: { requiresAuth: true }
  },
  // テストページのルートをコメントアウト
  /*
  {
    path: '/test-recording',
    name: 'TestRecording',
    component: TestRecording,
    meta: { requiresAuth: true }
  },
  {
    path: '/simple-test-recording',
    name: 'SimpleTestRecording',
    component: SimpleTestRecording,
    meta: { requiresAuth: true }
  },
  {
    path: '/test-recording-simple',
    name: 'TestRecordingSimple',
    component: TestRecordingSimple,
    meta: { requiresAuth: false }
  },
  */
  {
    path: '/mypage',
    name: 'MyPage',
    component: MyPage,
    meta: { requiresAuth: true }
  },
  {
    path: '/calendar',
    name: 'Calendar',
    component: Calendar,
    meta: { requiresAuth: true }
  },
  {
    path: '/cancel-subscription',
    name: 'CancelSubscription',
    component: CancelSubscription
  },
  // 認証後のデフォルトルート
  {
    path: '/dashboard',
    redirect: '/top'
  },
  // 講師用ルート
  {
    path: '/instructor/login',
    name: 'InstructorLogin',
    component: InstructorLogin
  },
  {
    path: '/instructor/signup',
    name: 'InstructorSignup',
    component: InstructorSignup
  },
  {
    path: '/instructor/home',
    name: 'InstructorHome',
    component: InstructorHome,
    meta: { requiresAuth: true, requiresInstructor: true }
  },
  {
    path: '/instructor/dashboard',
    name: 'InstructorDashboard',
    component: InstructorDashboard,
    meta: { requiresAuth: true, requiresInstructor: true }
  },
  {
    path: '/instructor/notification-settings',
    name: 'NotificationSettings',
    component: NotificationSettings,
    meta: { requiresAuth: true, requiresInstructor: true }
  },
  {
    path: '/instructor/vacation',
    name: 'VacationSettings',
    component: VacationSettings,
    meta: { requiresAuth: true, requiresInstructor: true }
  },
  {
    path: '/instructor/history',
    name: 'InstructorHistory',
    component: InstructorHistory,
    meta: { requiresAuth: true, requiresInstructor: true }
  },
  {
    path: '/instructor/mypage',
    name: 'InstructorMypage',
    component: InstructorMypage,
    meta: { requiresAuth: true, requiresInstructor: true }
  },
  {
    path: '/instructor/day0-submissions',
    name: 'InstructorDay0Submissions',
    component: InstructorDay0Submissions,
    meta: { requiresAuth: true, requiresInstructor: true }
  },
  // 管理画面ルート
  {
    path: '/admin/assignments',
    name: 'InstructorAssignments',
    component: InstructorAssignments,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/holidays',
    name: 'AdminHolidays',
    component: AdminHolidays,
    meta: { requiresAuth: true }
  },
  // 管理者（manager）専用
  {
    path: '/manager',
    name: 'ManagerDashboard',
    component: ManagerDashboard,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/instructors',
    name: 'ManagerInstructors',
    component: ManagerInstructors,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/submissions',
    name: 'ManagerSubmissions',
    component: ManagerSubmissions,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/feedback-playbacks',
    name: 'ManagerFeedbackPlaybacks',
    component: ManagerFeedbackPlaybacks,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/trial-progress',
    name: 'ManagerTrialProgress',
    component: ManagerTrialProgress,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/users',
    name: 'ManagerUsers',
    component: ManagerUsers,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/onboarding-progress',
    name: 'ManagerOnboardingProgress',
    component: ManagerOnboardingProgress,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/line-funnel',
    name: 'ManagerLineFunnel',
    component: ManagerLineFunnel,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/shikoku',
    name: 'ManagerShikokuFunnel',
    component: ManagerShikokuFunnel,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/processing',
    name: 'ManagerProcessingStatus',
    component: ManagerProcessingStatus,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/billing',
    name: 'ManagerBillingOverview',
    component: ManagerBillingOverview,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/learners',
    name: 'ManagerLearners',
    component: ManagerLearners,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/canceled-learners',
    name: 'ManagerCanceledLearners',
    component: ManagerCanceledLearners,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/manager/individual-analysis',
    name: 'ManagerIndividualAnalysis',
    component: ManagerIndividualAnalysis,
    meta: { requiresAuth: true, requiresManager: true }
  },
  // /training と /gallery 機能のルート
  {
    path: '/training',
    name: 'Training',
    component: Training,
    meta: { requiresAuth: true }
  },
  {
    path: '/training/day/:dayId',
    name: 'TrainingDay',
    component: TrainingDay,
    meta: { requiresAuth: true }
  },
  {
    path: '/training/task/:taskId',
    name: 'TrainingTask',
    component: TrainingTask,
    meta: { requiresAuth: true }
  },
  {
    path: '/gallery',
    name: 'Gallery',
    component: Gallery,
    meta: { requiresAuth: true }
  },
  {
    path: '/gallery/task/:taskId',
    name: 'GalleryTask',
    component: Gallery,
    meta: { requiresAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// ログインの段階別観測（指示書A7）。下の認証ガードより先に登録する。遷移の結果は変えない
attachLoginTelemetryToRouter(router)

// 認証ガード（本体）。下の router.beforeEach で、期限付きで呼ぶ
const authGuard = async (to, from, next) => {
  try {
  // 認証サーバーへの通信失敗は、未ログインと断定しない（回復画面へ）。セッションが無いと確認できたときだけ未ログイン
  // 確認結果は App・学習者一覧・Top と共有する（一遷移で1回。指示書 A3・④）
  const firstUserResult = await withTimeout(
    getAuthUserShared(),
    8000,
    'beforeEach: getUser timeout'
  )
  const firstUserKind = classifyGetUserResult(firstUserResult)
  if (firstUserKind === 'error') throw new GuardAccessError('get_user_failed', firstUserResult?.error)
  const sessionUser = firstUserKind === 'user' ? firstUserResult.data.user : null
  /** @type {{ profile: object, pastInitialSignupVerify: boolean, paid: boolean, hasActiveAppTrial: boolean, hasStripeCustomer: boolean, hasSubscriptionRow: boolean, hadAppTrialEverStarted?: boolean } | null} */
  let adultGate = null
  let sessionProfile = null
  if (sessionUser?.id) {
    // プロフィール行は新規登録時に DB で作られる。行が無いときだけ短く取り直す（指示書 A-24）。エラーは取り直さない
    const profileResult = await retryWhileMissing(() => supabase
      .from('users_phase2')
      .select('subscription_status, bypass_subscription, role, full_name, mic_check_completed, stripe_customer_id, email')
      .eq('id', sessionUser.id)
      .maybeSingle(), { attempts: 3, delayMs: 400 })
    assertQueryOk(profileResult, 'profile_query_failed')
    sessionProfile = profileResult.data
    // メールアドレスを変更した後なら、プロフィールの email を合わせる（待たない。遷移の判定には使わない）
    if (sessionProfile) {
      void syncProfileEmailIfChanged({ userId: sessionUser.id, authEmail: sessionUser.email, profileEmail: sessionProfile.email })
    }
    const roleEarly = sessionProfile?.role || 'adult'
    if (roleEarly === 'adult') {
      const { data: anySubRow } = assertQueryOk(await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', sessionUser.id)
        .limit(1)
        .maybeSingle(), 'subscriptions_query_failed')

      // アプリ内無料体験（Stripe 契約なし）中かを判定
      const nowIso = new Date().toISOString()
      const { data: activeAppTrialRow } = assertQueryOk(await supabase
        .from('learners')
        .select('id')
        .eq('parent_id', sessionUser.id)
        .eq('type', 'child')
        .not('signup_app_trial_ends_at', 'is', null)
        .gt('signup_app_trial_ends_at', nowIso)
        .limit(1)
        .maybeSingle(), 'app_trial_query_failed')

      const hasActiveAppTrial = !!activeAppTrialRow?.id

      // アプリ内体験を一度でも開始済み（終了後も ends_at が残る）→ verify 未実施扱いに戻さない
      const { data: anyAppTrialScheduledRow } = assertQueryOk(await supabase
        .from('learners')
        .select('id')
        .eq('parent_id', sessionUser.id)
        .eq('type', 'child')
        .not('signup_app_trial_ends_at', 'is', null)
        .limit(1)
        .maybeSingle(), 'app_trial_history_query_failed')
      const hadAppTrialEverStarted = !!anyAppTrialScheduledRow?.id

      const hasStripeCustomer = !!(sessionProfile?.stripe_customer_id && String(sessionProfile.stripe_customer_id).trim())
      const pastInitialSignupVerify =
        hasStripeCustomer || !!anySubRow || hasActiveAppTrial || hadAppTrialEverStarted
      // 利用できるかは、子ども単位の判定（契約・体験・バイパス。会員状態APIと共通）で決める（指示書 B）。
      // 親の subscription_status だけでは決めない（兄弟の一方の解約で、契約中の兄弟まで締め出さないため）。
      // 取得できなかったときは、未契約と扱わず回復画面へ
      const familyResult = await getFamilyMembership()
      if (familyResult.kind !== 'ok') throw new GuardAccessError('family_membership_failed')
      const paid = familyResult.family.any_access
      adultGate = {
        profile: sessionProfile,
        pastInitialSignupVerify,
        paid,
        hasActiveAppTrial,
        hadAppTrialEverStarted,
        hasStripeCustomer,
        hasSubscriptionRow: !!anySubRow?.id
      }
    }
  }

  // 初回専用 /signup/verify への直リンク対策（meta.requiresAuth が付いていないためここで処理）
  // 体験開始済み等はトライアル開始ボタンを再表示しない。Stripe 課金ユーザーはマイページ、アプリ内体験のみは /top（ガードがオンボ・マイクへ振り分け）
  if (sessionUser && to.path === '/signup/verify' && adultGate?.pastInitialSignupVerify) {
    const g = adultGate
    const appTrialOnly =
      g.hasActiveAppTrial && !g.hasStripeCustomer && !g.hasSubscriptionRow
    next(appTrialOnly ? '/top' : '/mypage')
    return
  }

  if (to.meta.requiresAuth) {
    // 冒頭で確認したユーザーを使う（同じ遷移の中で、認証サーバーへもう一度問い合わせない。指示書 ④）
    const user = sessionUser
    if (!user) {
      next('/')
      return
    }

    // 厳密フロー: 1.登録 → 2.メール認証 → 3./signup/verify → 4.Stripe決済 → 5.Onboarding
    // 各ステップが終わるまで他画面へ行かせない（講師/管理者は除く）
    const profile = sessionProfile
    const role = profile?.role || 'adult'
    const isAppUser = role === 'adult'
    const isStaffPath = to.path.startsWith('/instructor/') || to.path.startsWith('/manager/') || to.path.startsWith('/admin/')

    if (isAppUser && !isStaffPath && adultGate) {
      const paid = adultGate.paid
      const pastInitialSignupVerify = adultGate.pastInitialSignupVerify

      if (!paid) {
        if (pastInitialSignupVerify) {
          // 再開・解約済み等: 14日無料の signup/verify フローには戻さずマイページへ
          if (to.path !== '/mypage') {
            next('/mypage')
            return
          }
        } else {
          // ステップ4未了（初回のみ）: verify 系のみ（体験開始は /signup/verify の明示操作の後に paid 化）
          const allowedUnpaid =
            to.path === '/signup/verify' ||
            to.path === '/signup/complete' ||
            to.path === '/signup/success'
          if (!allowedUnpaid) {
            next('/signup/verify')
            return
          }
        }
      } else {
        // 初期登録（保護者名・お子さま情報）の入力がそろっていれば、通常どおり利用できる。
        // LINE連携はログインの条件にしない（初回の Onboarding 画面の中でだけ必須）
        const onboardingDone = await hasRequiredOnboardingInfo(user.id, profile)

        if (!onboardingDone) {
          // ステップ5未了: /onboarding 以外には行かせない
          if (to.path !== '/onboarding') {
            next('/onboarding')
            return
          }
        }
        // オンボーディング（保護者名・お子さま情報・LINE連携）完了後は
        // practice-setup を経由せず直接アプリを利用できる。
        // マイク確認は /top の「マイク設定確認」ボタンからセルフサービスで行う。
      }
    }

    // 講師権限チェック
    if (to.meta.requiresInstructor) {
      const { data: userProfile, error } = await supabase
        .from('users_phase2')
        .select('role')
        .eq('id', user.id)
        .single()
      // 取得失敗は「権限なし」ではない（例外→回復画面）
      if (error) throw new GuardAccessError('instructor_role_query_failed', error)
      if (userProfile?.role !== 'instructor') {
        console.warn('⚠️ 講師権限がありません:', { userId: user.id, role: userProfile?.role })
        alert('このページにアクセスする権限がありません。')
        next('/top')
        return
      }
    }

    // 管理者（manager）権限チェック
    if (to.meta.requiresManager) {
      const { data: userProfile, error } = await supabase
        .from('users_phase2')
        .select('role')
        .eq('id', user.id)
        .single()
      // 取得失敗は「権限なし」ではない（例外→回復画面）
      if (error) throw new GuardAccessError('manager_role_query_failed', error)
      if (userProfile?.role !== 'manager') {
        console.warn('⚠️ 管理者権限がありません:', { userId: user.id, role: userProfile?.role })
        alert('このページにアクセスする権限がありません。')
        next('/top')
        return
      }
    }
  }
  
  // 認証済みユーザーがルート（/）にアクセスした場合は
  // 「支払い状況 / Onboarding / マイク確認」の状態を見て適切な画面へリダイレクト
  if (to.path === '/') {
    if (sessionUser && adultGate) {
      const profile = adultGate.profile
      const paid = adultGate.paid
      const pastInitialSignupVerify = adultGate.pastInitialSignupVerify

      if (!paid) {
        if (pastInitialSignupVerify) {
          next('/mypage')
          return
        }
        next('/signup/verify')
        return
      }

      // 初期登録（保護者名・お子さま情報）の入力がそろっているか。LINE連携は確かめない
      const onboardingDone = await hasRequiredOnboardingInfo(sessionUser.id, profile)
      if (!onboardingDone) {
        next('/onboarding')
        return
      }

      next('/top')
      return
    }
    if (sessionUser && !adultGate) {
      // 講師・管理者など（保護者ゲート対象外）
      next('/top')
      return
    }
  }

  next()
  } catch (err) {
    console.error('[router] beforeEach error:', err)
    Sentry.captureException(err)
    markLoginStage('access_check_failed', { reason: err?.reason || 'guard_exception' })
    // 取得の失敗・通信の不調を、未ログイン・未契約・未連携と扱わない（指示書 A4）。
    // 保護されたページと / は回復画面へ。回復画面はガードの確認なしで通るので、ループしない。
    // それ以外の公開ページは、そのまま表示する
    next(guardErrorExit(to, (p) => safeInternalReturnPath(p, '/top')))
  }
}

router.beforeEach((to, from, next) => {
  // 回復画面は、認証・契約・LINEの確認より先に通す（回復画面が同じ確認で再び失敗しないように）
  if (to.name === 'ConnectionError') {
    next()
    return
  }
  // ガードの中の通信が返ってこなくても、期限で必ず終わらせる（指示書 A-06・A-25）。
  // 保護されたページとログイン入口は回復画面へ。それ以外の公開ページ（パスワード再設定等）は、そのまま表示する
  const needsRecoveryExit = Boolean(to.meta?.requiresAuth) || to.path === '/'
  const settler = createGuardSettler(next, {
    onTimeout: () => {
      // 止まった家族APIの取得を、回復画面からの再試行で待ち直さない（次は新しく取得する。指示書 B）
      abandonFamilyMembershipInFlight()
      markLoginStage('access_check_failed', { reason: 'guard_deadline', route: to.name ? String(to.name) : undefined })
      reportGuardDeadline({ routeName: to.name ? String(to.name) : undefined })
      if (!needsRecoveryExit) return undefined
      return { name: 'ConnectionError', query: { to: safeInternalReturnPath(to.fullPath, '/top') } }
    }
  })
  authGuard(to, from, settler.next).catch((err) => {
    console.error('[router] authGuard unexpected error:', err)
    settler.next(to.path === '/' ? undefined : '/')
  })
})

// デプロイ直後、古いタブが差し替え済みのJSチャンクを読みに行って失敗するケースの救済。
// 該当エラーのみ検知し、無限ループ防止のため1回だけ再読み込みする。
router.onError((error, to) => {
  const msg = error?.message || ''
  if (!isStaleChunkErrorMessage(msg)) return

  const reloadKey = `chunk-reload-${to.fullPath}`
  if (sessionStorage.getItem(reloadKey)) return

  sessionStorage.setItem(reloadKey, '1')
  window.location.href = to.fullPath
})

export default router
